"use client"

import type { ReactNode } from "react"
import { Button } from "./Button"

type Variant = "centered" | "inline" | "success"

const VARIANT_STYLE: Record<
  Variant,
  {
    container: string
    iconWrap: string
    iconBg: string
    iconColor: string
  }
> = {
  centered: {
    container: "text-center py-12 px-6",
    iconWrap: "mx-auto w-14 h-14 rounded-full",
    iconBg: "var(--redy-warm-white)",
    iconColor: "var(--redy-muted)",
  },
  inline: {
    container:
      "py-5 px-5 flex items-center gap-3 bg-[var(--redy-surface)] border border-[var(--redy-border)] rounded-[14px]",
    iconWrap: "w-10 h-10 rounded-[10px] shrink-0",
    iconBg: "var(--redy-warm-white)",
    iconColor: "var(--redy-muted)",
  },
  success: {
    container:
      "py-5 px-5 flex items-center gap-3 bg-[var(--redy-surface)] border border-[var(--redy-border)] rounded-[14px] [box-shadow:var(--shadow-card)]",
    iconWrap: "w-10 h-10 rounded-[10px] shrink-0",
    iconBg: "var(--redy-success-bg)",
    iconColor: "var(--redy-success)",
  },
}

export type EmptyStateProps = {
  icon?: ReactNode
  title: string
  description?: string
  cta?: {
    label: string
    href?: string
    onClick?: () => void
    variant?: "primary" | "secondary"
  }
  secondaryCta?: {
    label: string
    href?: string
    onClick?: () => void
  }
  variant?: Variant
  className?: string
}

export function EmptyState({
  icon,
  title,
  description,
  cta,
  secondaryCta,
  variant = "centered",
  className = "",
}: EmptyStateProps) {
  const v = VARIANT_STYLE[variant]
  const isCentered = variant === "centered"

  const iconNode = icon && (
    <div
      className={`${v.iconWrap} flex items-center justify-center`}
      style={{ background: v.iconBg, color: v.iconColor }}
      aria-hidden
    >
      {icon}
    </div>
  )

  const textNode = (
    <div className={isCentered ? "mt-4" : "flex-1 min-w-0"}>
      <p className="text-base font-bold text-[var(--redy-charcoal)]">{title}</p>
      {description && (
        <p
          className={`text-[13px] text-[var(--redy-secondary)] mt-1.5 ${
            isCentered ? "max-w-sm mx-auto" : ""
          }`}
        >
          {description}
        </p>
      )}
    </div>
  )

  const ctaNode = (cta || secondaryCta) && (
    <div
      className={
        isCentered ? "mt-5 flex flex-wrap items-center justify-center gap-3" : "shrink-0 flex items-center gap-2"
      }
    >
      {cta &&
        (cta.href ? (
          <Button href={cta.href} variant={cta.variant ?? "primary"} size="md">
            {cta.label}
          </Button>
        ) : (
          <Button
            onClick={cta.onClick}
            variant={cta.variant ?? "primary"}
            size="md"
          >
            {cta.label}
          </Button>
        ))}
      {secondaryCta &&
        (secondaryCta.href ? (
          <Button href={secondaryCta.href} variant="ghost" size="md">
            {secondaryCta.label}
          </Button>
        ) : (
          <Button onClick={secondaryCta.onClick} variant="ghost" size="md">
            {secondaryCta.label}
          </Button>
        ))}
    </div>
  )

  return (
    <div className={`${v.container} ${className}`}>
      {iconNode}
      {textNode}
      {ctaNode}
    </div>
  )
}
